import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import reduce from 'lodash/reduce';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';

import { EXTRA_ADDON_TYPES, GIFTWRAP_ADDON_TYPES, getAddonsOfType } from 'src/common/utils/addons';
import { replaceRichText } from 'src/common/utils/text-replace';
import useOrder from '../../hooks/use-order';
import useGlobalContent from '../../hooks/use-global-content';

import Panel from 'wonderbly-components/lib/Panel';
import OrderImages from '../Order/OrderImages';
import RichText from '../Prismic/RichText';
import Loading from '../Loading';

import './OrderLineItems.scss';

const translateCustomisation = customisation => {
  let { customisation: translatedCustomisation } = useGlobalContent('orders');

  translatedCustomisation = translatedCustomisation.reduce((result, { id, label }) => {
    if (!id || !label) {
      return result;
    }

    const [ group, key ] = id.split('::');
    if (key) {
      result[group] = {
        ...result[group],
        key: label,
      };
    } else {
      // Very unlikely to happen but just in case it does we want to be able to handle it and not
      // crash the site.
      result[group] = label;
    }

    return result;
  }, {});

  return reduce(customisation, (result, value, key) => {
    result[key] = get(translatedCustomisation, `${key}.${value}`) || value;
    return result;
  }, {});
};

const translatedAddons = item => {
  if (!item.components) {
    return [];
  }

  const translations = get(item, 'cart.options.cart.addOn') || [];
  const byTypeAndDesign = (type, design) => item => item.type === type && item.design === design;

  return orderBy(item.components.map(component => {
    let addon = {
      id: component.id,
      image: { url: '' },
      label: component.id
    };

    // Long time ago, only one giftwrap existed for the whole company, with the sku of 'lmn:giftwrap'.
    // It was a monkey-styled giftwrap.
    // Since then, many more giftwraps were created, with better-structured SKUs. But the original
    // monkey giftwrap stayed as 'lmn:giftwrap'.
    // Prismic came later, and Prismic does not have a generic 'lmn:giftwrap' SKU understanding. Prismic only has
    // properly structured keys, which include the context of the item (i.e monkey-giftwrap is a monkey giftwrap).
    // Because it's like that in Prod, we can't change the existing Prismic key to match the eagle generic lmn:giftwrap one, and we can't change eagle to have another SKU.
    // True as of November 2019 - Edwin, Lev
    if (component.id === 'lmn:giftwrap') {
      return {
        ...addon,
        ...translations.find(byTypeAndDesign('giftwrap', 'monkey')),
      };
    }

    // This is assuming that the component ID will always be structure in the following way `type:size:design`.
    // Any other component structure will this flow.
    // We can't change the structure now as it wouldn't be backwards compatible for existing SKUs.
    // Ideally, one would simply forward the SKU to Prismic, and get the translation in response.
    // Nov 2019 - Edwin, Lev
    const idSplit = component.id.split(':');
    const type = idSplit[0];
    const design = idSplit.pop();

    return {
      ...addon,
      ...translations.find(byTypeAndDesign(type, design)),
    };
  }), ['type'], ['desc']);
};

const OrderLineItems = ({ inscriptionLabel }) => {
  const { orderNumber } = useParams();
  const { linkedDocuments = {}, error: contentError = {} } = useGlobalContent();
  const { options = {} } = linkedDocuments;
  const { http = {} } = contentError;
  const [ formats, setFormats ] = useState({});

  useEffect(() => {
    setFormats(
      get(options, 'cart.bookType', []).reduce(
        (acc, item) => ({ ...acc, [item.bookType]: item }),
        {}
      )
    );
  }, [options]);

  const { data, loading, error } = useOrder(orderNumber);

  return (
    <div className="order-line-items">
      {(loading || error) && <Panel border="shadow">
        {loading && (
          <Panel.Section>
            <Loading small relative dark />
          </Panel.Section>
        )}
        {error && (
          <Panel.Section>
            {http['500']}
          </Panel.Section>
        )}
      </Panel>}

      {data && data.items.map(item => {
        const customisationLabel = get(item, 'order.customisationLabel', '');
        const customisationContent = [{ type: 'paragraph', text: customisationLabel }];
        const addons = translatedAddons(item);
        const giftwrapAddons = getAddonsOfType(addons, GIFTWRAP_ADDON_TYPES);
        const extraAddons = getAddonsOfType(addons, EXTRA_ADDON_TYPES);
        const hasGiftwrapAddons = !isEmpty(giftwrapAddons);
        const hasExtraAddons = !isEmpty(extraAddons);
        return (
          <Panel border="shadow" className="order-line-items__line-item" key={item.id}>
            <OrderImages items={[item]}/>
            <Panel.Section>
              <h3 className="order-line-items__line-item__title">
                {get(item, 'productInformation.title') || `Product SKU: ${item.productSku}`}
              </h3>
              {customisationLabel &&
                <RichText
                  className="order-line-items__line-item__customisation"
                  fields={replaceRichText(customisationContent, translateCustomisation(item.customisation))}
                />
              }
            </Panel.Section>
            {item.customisation.inscription && (
              <Panel.Section>
                <h4 className="order-line-items__line-item__dedication-title">
                  {inscriptionLabel}
                </h4>
                <div className="order-line-items__line-item__dedication-content">
                  &quot;{item.customisation.inscription}&quot;
                </div>
              </Panel.Section>
            )}
            <Panel.Section>
              <div className="order-line-items__line-item__details">
                <div>
                  {get(formats,`${item.formatIdentifier}.label`) || `Format SKU: ${item.formatIdentifier}`}
                  {hasGiftwrapAddons && (
                    <span className="order-line-items__line-item__details__addons order-line-items__line-item__details__addons--giftwrap">
                      + {giftwrapAddons.map(addon => addon.label).join(' + ')}
                    </span>
                  )}
                  {hasExtraAddons && (
                    <span className="order-line-items__line-item__details__addons order-line-items__line-item__details__addons--extra">
                      + {extraAddons.map(addon => addon.label).join(' + ')}
                    </span>
                  )}
                </div>
                <div className="order-line-items__line-item__details__price">
                  {item.price.formatted}
                </div>
              </div>
            </Panel.Section>
            {/* TODO: Uncomment this when we fix edit url */}
            {/*{allowEdit(item) && (*/}
            {/*  <Panel.Section>*/}
            {/*    <OrderButtons>*/}
            {/*      <OrderButtons.Edit*/}
            {/*        number={data.number}*/}
            {/*        state={data.state}*/}
            {/*        processAt={data.processAt}*/}
            {/*        {...item}*/}
            {/*      />*/}
            {/*    </OrderButtons>*/}
            {/*  </Panel.Section>*/}
            {/*)}*/}
          </Panel>
        );
      })}
    </div>
  );
};

OrderLineItems.propTypes = {
  inscriptionLabel: PropTypes.string.isRequired,
};

export default OrderLineItems;



// WEBPACK FOOTER //
// ./src/client/js/view/components/OrderLineItems/OrderLineItems.js